import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { staffAuthService } from '../../../lib/staffAuthService';
import { QRCodeSVG } from 'qrcode.react';

const PersonalInformationTab = () => {
  const { user, userProfile, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
  });
  const [pinSection, setPinSection] = useState({
    showPinSetup: false,
    newPin: '',
    confirmPin: '',
    hasPinSet: false,
    qrCodeData: null
  });

  useEffect(() => {
    if (userProfile) {
      setFormData({
        firstName: userProfile?.first_name || '',
        lastName: userProfile?.last_name || '',
        phone: userProfile?.phone || '',
        email: userProfile?.email || '',
      });
      
      // Check if user is staff/trainer/manager and load PIN status
      if (['staff', 'trainer', 'manager']?.includes(userProfile?.role)) {
        loadPINStatus();
      }
    }
  }, [userProfile]);

  const loadPINStatus = async () => {
    const { data: hasPinSet } = await staffAuthService?.hasPINSet(user?.id);
    const { data: qrData } = await staffAuthService?.getStaffQRCode(user?.id);
    
    setPinSection(prev => ({
      ...prev,
      hasPinSet: hasPinSet || false,
      qrCodeData: qrData?.qr_code_identifier ? `STAFF_QR_${qrData?.qr_code_identifier}` : null
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors?.[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (formData?.phone && !/^[\d\s()+-]+$/?.test(formData?.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setSuccessMessage('');

    try {
      const updates = {
        first_name: formData?.firstName?.trim(),
        last_name: formData?.lastName?.trim(),
        full_name: `${formData?.firstName?.trim()} ${formData?.lastName?.trim()}`,
        phone: formData?.phone?.trim() || null,
      };

      const { error } = await updateProfile(updates);

      if (error) {
        setErrors({ general: error?.message || 'Failed to update profile' });
      } else {
        setSuccessMessage('Profile updated successfully!');
        setIsEditing(false);
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePinSetup = async () => {
    // Validate PIN
    if (!/^\d{6}$/?.test(pinSection?.newPin)) {
      setErrors({ pin: 'PIN must be exactly 6 digits' });
      return;
    }

    if (pinSection?.newPin !== pinSection?.confirmPin) {
      setErrors({ pin: 'PINs do not match' });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const { error } = await staffAuthService?.setStaffPIN(user?.id, pinSection?.newPin);

      if (error) {
        setErrors({ pin: error?.message || 'Failed to set PIN' });
      } else {
        setSuccessMessage('PIN set successfully!');
        setPinSection({
          showPinSetup: false,
          newPin: '',
          confirmPin: '',
          hasPinSet: true,
          qrCodeData: pinSection?.qrCodeData
        });
        setTimeout(() => setSuccessMessage(''), 3000);
        await loadPINStatus();
      }
    } catch (error) {
      setErrors({ pin: 'An unexpected error occurred' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: userProfile?.first_name || '',
      lastName: userProfile?.last_name || '',
      phone: userProfile?.phone || '',
      email: userProfile?.email || '',
    });
    setErrors({});
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-success/10 border border-success/20">
          <Icon name="CheckCircle" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
          <p className="text-sm text-success">{successMessage}</p>
        </div>
      )}
      {/* Error Message */}
      {errors?.general && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-error/10 border border-error/20">
          <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error">{errors?.general}</p>
        </div>
      )}
      {/* Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="First Name"
          type="text"
          name="firstName"
          placeholder="Enter your first name"
          value={formData?.firstName}
          onChange={handleChange}
          error={errors?.firstName}
          disabled={!isEditing || isLoading}
          required
        />

        <Input
          label="Last Name"
          type="text"
          name="lastName"
          placeholder="Enter your last name"
          value={formData?.lastName}
          onChange={handleChange}
          error={errors?.lastName}
          disabled={!isEditing || isLoading}
          required
        />

        <Input
          label="Email Address"
          type="email"
          name="email"
          value={formData?.email}
          disabled
          description="Email cannot be changed"
        />

        <Input
          label="Phone Number"
          type="tel"
          name="phone"
          placeholder="(555) 123-4567"
          value={formData?.phone}
          onChange={handleChange}
          error={errors?.phone}
          disabled={!isEditing || isLoading}
        />
      </div>
      {/* Role Information */}
      <div className="pt-6 border-t border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">Account Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-muted-foreground">Role</label>
            <p className="mt-1 text-foreground capitalize">{userProfile?.role || 'member'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">Account Status</label>
            <p className="mt-1">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                userProfile?.is_active
                  ? 'bg-success/10 text-success' :'bg-error/10 text-error'
              }`}>
                {userProfile?.is_active ? 'Active' : 'Inactive'}
              </span>
            </p>
          </div>
        </div>
      </div>
      {/* PIN Setup for Staff/Trainer/Manager */}
      {['staff', 'trainer', 'manager']?.includes(userProfile?.role) && (
        <div className="pt-6 border-t border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Lock" size={20} className="text-primary" />
            Staff Authentication PIN
          </h3>
          
          {!pinSection?.showPinSetup ? (
            <div className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">
                      PIN Status: {pinSection?.hasPinSet ? (
                        <span className="text-success">Set</span>
                      ) : (
                        <span className="text-warning">Not Set</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {pinSection?.hasPinSet 
                        ? 'Your 6-digit PIN is configured for QR authentication'
                        : 'Set up a PIN to use QR code authentication'}
                    </p>
                  </div>
                  <Button
                    onClick={() => setPinSection(prev => ({ ...prev, showPinSetup: true }))}
                    variant="outline"
                    size="sm"
                  >
                    <Icon name="Edit" size={16} />
                    <span>{pinSection?.hasPinSet ? 'Change PIN' : 'Set PIN'}</span>
                  </Button>
                </div>
              </div>

              {/* QR Code Display */}
              {pinSection?.qrCodeData && (
                <div className="p-6 bg-white rounded-lg border border-border">
                  <h4 className="text-sm font-semibold text-foreground mb-4 text-center">
                    Your Staff QR Code
                  </h4>
                  <div className="flex justify-center mb-4">
                    <QRCodeSVG
                      value={pinSection?.qrCodeData}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                  <p className="text-xs text-center text-muted-foreground">
                    Use this QR code with your PIN for staff authentication
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={18} className="text-primary flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">PIN Requirements</p>
                    <ul className="space-y-1">
                      <li>• Must be exactly 6 digits</li>
                      <li>• Used with QR code for secure authentication</li>
                      <li>• Account locks after 5 failed attempts</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Input
                label="New PIN"
                type="password"
                inputMode="numeric"
                maxLength={6}
                placeholder="Enter 6-digit PIN"
                value={pinSection?.newPin}
                onChange={(e) => {
                  const value = e?.target?.value?.replace(/\D/g, '');
                  setPinSection(prev => ({ ...prev, newPin: value }));
                  if (errors?.pin) setErrors({});
                }}
                error={errors?.pin}
                disabled={isLoading}
              />

              <Input
                label="Confirm PIN"
                type="password"
                inputMode="numeric"
                maxLength={6}
                placeholder="Re-enter 6-digit PIN"
                value={pinSection?.confirmPin}
                onChange={(e) => {
                  const value = e?.target?.value?.replace(/\D/g, '');
                  setPinSection(prev => ({ ...prev, confirmPin: value }));
                  if (errors?.pin) setErrors({});
                }}
                disabled={isLoading}
              />

              <div className="flex gap-3">
                <Button
                  onClick={handlePinSetup}
                  disabled={isLoading || !pinSection?.newPin || !pinSection?.confirmPin}
                  loading={isLoading}
                >
                  <Icon name="Save" size={16} />
                  <span>Save PIN</span>
                </Button>
                <Button
                  onClick={() => setPinSection(prev => ({
                    ...prev,
                    showPinSetup: false,
                    newPin: '',
                    confirmPin: ''
                  }))}
                  variant="outline"
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex items-center gap-3 pt-6 border-t border-border">
        {!isEditing ? (
          <Button
            onClick={() => setIsEditing(true)}
            variant="default"
            iconName="Edit"
          >
            Edit Profile
          </Button>
        ) : (
          <>
            <Button
              onClick={handleSave}
              variant="default"
              loading={isLoading}
              iconName="Save"
            >
              Save Changes
            </Button>
            <Button
              onClick={handleCancel}
              variant="outline"
              disabled={isLoading}
            >
              Cancel
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PersonalInformationTab;